import * as React from "react";

import Link from "next/link";

import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@/shared/helpers/cn.helper";

import Typography from "../typography/typography";

const baseClasses = [
  "flex items-center gap-2.5 cursor-pointer p-2 select-none",
  "border border-transparent rounded-lg",
  "hover:text-light-600 hover:bg-white",
  "active:border-light-600",
];

const listOptionVariants = cva(baseClasses, {
  variants: {
    size: {
      big: "py-2",
      small: [
        "h-9 max-h-9 min-h-9",
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.sMedium,
      ],
    },
  },
  defaultVariants: {
    size: "big",
  },
});

type IListOptionSharedProps = VariantProps<typeof listOptionVariants> & {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  className?: string;
};

type IListOptionProps = IListOptionSharedProps &
  (
    | ({ href?: never } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">)
    | ({ href: string } & Omit<React.ComponentProps<typeof Link>, "children" | "className" | "href">)
  );

export const ListOption: React.FC<IListOptionProps> = ({
  icon: Icon,
  label,
  size,
  className,
  href,
  ...props
}) => {
  const classes = cn("group", listOptionVariants({ size }), className);

  const graphic = Icon ? (
    <Icon
      className={cn("group-hover:text-inherit", size === "big" ? "h-6 w-6" : "h-5 w-5")}
    />
  ) : null;

  if (href) {
    return (
      <Link
        className={classes}
        href={href}
        {...(props as Omit<React.ComponentProps<typeof Link>, "children" | "className" | "href">)}
      >
        {graphic}
        {label}
      </Link>
    );
  }

  return (
    <button
      className={classes}
      type="button"
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {graphic}
      {label}
    </button>
  );
};
